<script setup>
/* eslint-disable no-shadow, vue/no-unused-properties, no-console, vue/custom-event-name-casing */
import {
  onMounted,
  computed,
  ref,
  toRefs,
  useTemplateRef,
  nextTick,
} from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { getTemplateInfo } from './templateHelper.js';
import { useTrack } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import { buildGroupAuthor, groupAuthorLabels } from './groupAuthorPresenter.js';

import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import GroupAuthorAvatar from './GroupAuthorAvatar.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import NPSBubble from './bubbles/NPS.vue';
import FormBubble from './bubbles/Form.vue';

import MessageError from './MessageError.vue';
import MessageReactions from './MessageReactions.vue';
import MessageEditInput from './MessageEditInput.vue';
import MessageEditHistory from './MessageEditHistory.vue';
import MessageDeleteHistory from './MessageDeleteHistory.vue';
import ReferralCard from './ReferralCard.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import MessageAPI from 'dashboard/api/inbox/message';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import RestrictedMessage from './RestrictedMessage.vue';
import { useBranding } from 'shared/composables/useBranding';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  groupWithPrevious: { type: Boolean, default: false },
  isGroupConversation: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
  reactions: { type: Array, default: () => [] },
  /**
   * Reações do WhatsApp que chegaram como mensagem e foram agregadas pelo MessageList
   * (`[{ emoji, count, title }]`). Vazio em toda mensagem que não foi reagida — ou seja,
   * na esmagadora maioria, onde nada muda.
   */
  whatsappReactions: { type: Array, default: () => [] },
  forwarded: { type: Boolean, default: false },
  starred: { type: Boolean, default: false },
  isWahaInbox: { type: Boolean, default: false },
  isForwardMode: { type: Boolean, default: false },
  isForwardSelected: { type: Boolean, default: false },
  isNew: { type: Boolean, default: false },
  teamId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  agentId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
});

const emit = defineEmits(['forward', 'toggle-forward-selection']);

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const isEditing = ref(false);
const isHistoryOpen = ref(false);
const historyTriggerRect = ref(null);
const isDeleteHistoryOpen = ref(false);
const deleteHistoryTriggerRect = ref(null);
const dropdownButtonRef = useTemplateRef('dropdownButtonRef');
const { t } = useI18n();
const route = useRoute();
const store = useStore();
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const { replaceInstallationName } = useBranding();

// Team isolation: check if message should be restricted
const currentChat = computed(() => store.getters.getSelectedChat || {});
const currentInbox = computed(() => {
  const inboxId = currentChat.value?.inbox_id;
  return inboxId ? store.getters['inboxes/getInbox'](inboxId) : {};
});
// Membro ∪ supervisionado (getter da F4). Sem ninguém supervisionando isto é
// idêntico a getMyTeams — o agente comum continua vendo o cadeado.
const visibleTeams = computed(() => {
  const teams = store.getters['teams/getVisibleTeams'] || [];
  return teams.map(team => team.id);
});
const supervisedTeams = computed(() => {
  const teams = store.getters['teams/getSupervisedTeams'] || [];
  return teams.map(team => team.id);
});
// O backend (F2) isenta o supervisor por CONVERSA: se o time da conversa aberta é
// supervisionado, o MessageFinder devolve todas as bolhas dela. O front tem que
// usar o mesmo eixo, senão mascara justamente o que o servidor destravou.
const isSupervisedChat = computed(() => {
  if (!supervisedTeams.value.length) return false;
  const chatTeamId =
    currentChat.value?.meta?.team?.id || currentChat.value?.team_id || null;
  return !!chatTeamId && supervisedTeams.value.includes(chatTeamId);
});
const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const isAdmin = computed(() => {
  const user = store.getters.getCurrentUser || {};
  const accountId = currentAccount.value?.id;
  const accountUser = user.accounts?.find(a => a.id === accountId);
  return accountUser?.role === 'administrator';
});
const teamIsolationEnabled = computed(
  () => currentInbox.value?.team_message_isolation === true
);
const allTeams = computed(() => store.getters['teams/getTeams'] || []);
const restrictedTeamName = computed(() => {
  if (!props.teamId) return '';
  const team = allTeams.value.find(t => t.id === props.teamId);
  return team?.name || `#${props.teamId}`;
});
const isRestrictedByTeam = computed(() => {
  if (!teamIsolationEnabled.value) return false;
  if (!props.teamId) return false;
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (isAdmin.value) return false;
  if (isSupervisedChat.value) return false;
  return !visibleTeams.value.includes(props.teamId);
});

// Agent isolation: depende de QUEM olha — restrito se a msg pertence a OUTRO agente.
const agentIsolationEnabled = computed(
  () => currentInbox.value?.agent_message_isolation === true
);
const allAgents = computed(() => store.getters['agents/getAgents'] || []);
const restrictedAgentName = computed(() => {
  if (!props.agentId) return '';
  const agent = allAgents.value.find(a => a.id === props.agentId);
  return agent?.name || `#${props.agentId}`;
});
const isRestrictedByAgent = computed(() => {
  if (!agentIsolationEnabled.value) return false;
  if (!props.agentId) return false;
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (isAdmin.value) return false;
  if (isSupervisedChat.value) return false;
  return props.agentId !== props.currentUserId;
});

// Bolha 🔒 unificada (team OU agente). Team tem precedência no rótulo quando ambos
// disparam — em qualquer caso o conteúdo fica oculto do mesmo jeito.
const isRestricted = computed(
  () => isRestrictedByTeam.value || isRestrictedByAgent.value
);
const restrictedTitle = computed(() =>
  isRestrictedByTeam.value
    ? t('CONVERSATION.TEAM_ISOLATION.RESTRICTED_TITLE', {
        team: restrictedTeamName.value,
      })
    : t('CONVERSATION.AGENT_ISOLATION.RESTRICTED_TITLE', {
        agent: restrictedAgentName.value,
      })
);
const restrictedSubtitle = computed(() =>
  isRestrictedByTeam.value
    ? t('CONVERSATION.TEAM_ISOLATION.RESTRICTED_SUBTITLE')
    : t('CONVERSATION.AGENT_ISOLATION.RESTRICTED_SUBTITLE')
);

const EDIT_WINDOW_SECONDS = 900;

const isWithinEditWindow = computed(() => {
  if (!props.createdAt) return false;
  return Date.now() / 1000 - props.createdAt < EDIT_WINDOW_SECONDS;
});

const isSentByCurrentUser = computed(() => {
  return (props.senderId || props.sender?.id) === props.currentUserId;
});

const scheduledBy = computed(() => props.contentAttributes?.scheduledBy);
const isScheduledMessage = computed(
  () => !!scheduledBy.value || !!props.contentAttributes?.scheduledAt
);
const scheduledByName = computed(
  () =>
    scheduledBy.value?.name ||
    props.sender?.name ||
    t('CONVERSATION.SCHEDULED_BY_UNKNOWN')
);
const scheduledByLabel = computed(() =>
  t('CONVERSATION.SCHEDULED_BY', { name: scheduledByName.value })
);

// WhatsApp approved template (HSM): outgoing messages carrying `template_params`. Without
// this the agent cannot tell a template apart from a message someone typed by hand.
const templateInfo = computed(() =>
  getTemplateInfo({
    messageType: props.messageType,
    additionalAttributes: props.additionalAttributes,
  })
);
const templateBadgeLabel = computed(() => {
  if (!templateInfo.value) return '';

  return templateInfo.value.name
    ? t('CONVERSATION.TEMPLATE_BADGE_NAMED', { name: templateInfo.value.name })
    : t('CONVERSATION.TEMPLATE_BADGE');
});

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'scheduled'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;
  if (
    isScheduledMessage.value &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return MESSAGE_VARIANTS.SCHEDULED;
  }

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    (!props.sender && !props.additionalAttributes?.senderName);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "avatar bubble"
      "spacer meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;

  return true;
});

const componentToRender = computed(() => {
  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_NPS) {
    return NPSBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  // One or more shared contacts render as rich contact cards. Content may hold
  // the contact name, so this is intentionally not gated on `!props.content`.
  if (
    Array.isArray(props.attachments) &&
    props.attachments.length >= 1 &&
    props.attachments.every(
      attachment => attachment.fileType === ATTACHMENT_TYPES.CONTACT
    )
  ) {
    return ContactBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const isForwardSelectable = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS
  ) {
    return false;
  }
  return !isMessageDeleted.value;
});

const forwardSelectClass = computed(() => ({
  'mr-2': orientation.value === ORIENTATION.LEFT,
  'ml-2': orientation.value === ORIENTATION.RIGHT,
}));

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
  };
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;
  const isActivity = props.messageType === MESSAGE_TYPES.ACTIVITY;
  const canEditBase =
    isOutgoing &&
    hasText &&
    !isMessageDeleted.value &&
    !isFailedOrProcessing &&
    props.isWahaInbox;

  return {
    copy: hasText,
    delete:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value &&
      props.contentType !== CONTENT_TYPES.INPUT_NPS &&
      props.contentType !== CONTENT_TYPES.INPUT_CSAT,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
    forward: !isActivity && !isFailedOrProcessing && !isMessageDeleted.value,
    star: !isActivity && !isFailedOrProcessing && !isMessageDeleted.value,
    edit: canEditBase && isSentByCurrentUser.value && isWithinEditWindow.value,
    editExpired:
      canEditBase && isSentByCurrentUser.value && !isWithinEditWindow.value,
    editNotSender: canEditBase && !isSentByCurrentUser.value,
  };
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError
  );
});

function openContextMenu(e) {
  if (props.isForwardMode) return;
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a', 'img'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

function openHoverDropdown(e) {
  if (props.isForwardMode) return;
  if (!dropdownButtonRef.value) return;

  e.preventDefault();
  e.stopPropagation();

  nextTick(() => {
    // Usa o elemento do botão para calcular a posição
    const buttonComponent = dropdownButtonRef.value;
    const buttonEl = buttonComponent?.$el || buttonComponent;
    if (!buttonEl || !buttonEl.getBoundingClientRect) return;

    const rect = buttonEl.getBoundingClientRect();

    // Posição abaixo da seta (botão)
    // Alinha à direita do botão
    // O ContextMenu usa position: fixed, então usamos coordenadas da viewport
    // O ContextMenu ajustará automaticamente se o menu ultrapassar os limites
    const x = rect.right;

    contextMenuPosition.value = {
      x: x,
      y: rect.bottom + 4, // 4px de espaçamento abaixo da seta
    };

    showContextMenu.value = true;
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  });
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

function handleForward() {
  emit('forward', { messageId: props.id });
  useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
}

function toggleForwardSelection() {
  if (!props.isForwardMode || !isForwardSelectable.value) return;
  emit('toggle-forward-selection', props.id);
}

function handleEdit() {
  showContextMenu.value = false;
  isEditing.value = true;
}

async function handleStar() {
  try {
    const accountId = store.getters.getCurrentAccountId;
    await MessageAPI.starMessage({
      accountId,
      conversationId: props.conversationId,
      messageId: props.id,
    });
    useAlert(t('CONVERSATION.STAR_SUCCESS') || 'Mensagem favoritada');
  } catch (error) {
    console.error('Error starring message:', error);
    useAlert(t('CONVERSATION.STAR_ERROR') || 'Erro ao favoritar mensagem');
  }
}

async function handleUnstar() {
  try {
    const accountId = store.getters.getCurrentAccountId;
    await MessageAPI.unstarMessage({
      accountId,
      conversationId: props.conversationId,
      messageId: props.id,
    });
    useAlert(t('CONVERSATION.UNSTAR_SUCCESS') || 'Mensagem desfavoritada');
  } catch (error) {
    console.error('Error unstarring message:', error);
    useAlert(t('CONVERSATION.UNSTAR_ERROR') || 'Erro ao desfavoritar mensagem');
  }
}

// Guards against double-firing the retry: rapid clicks (or a click while the
// previous retry is still in flight) would otherwise POST /retry twice and
// duplicate the outgoing message. The flag also drives the disabled state +
// spinner on the retry button.
const isRetrying = ref(false);

async function handleRetry() {
  if (isRetrying.value) return;
  isRetrying.value = true;
  try {
    await MessageAPI.retry(props.conversationId, props.id);
    // A mensagem será atualizada automaticamente via websocket
  } catch (error) {
    console.error('Error retrying message:', error);
    useAlert(
      t('CONVERSATION.RETRY_ERROR') || 'Erro ao tentar reenviar mensagem'
    );
  } finally {
    isRetrying.value = false;
  }
}

/**
 * Autor da mensagem dentro do grupo, gravado na ingestão
 * (`content_attributes.group_sender`, camelizado pelo MessageList).
 */
const groupSender = computed(() => {
  const attrs = props.contentAttributes || {};
  return attrs.groupSender ?? attrs.group_sender ?? null;
});

/**
 * Identidade visual do autor na bolha de grupo: rótulo, iniciais, cor determinística
 * (light e dark) e tooltip.
 *
 * Só existe em conversa de grupo E mensagem recebida — a conversa 1:1 nunca passa por
 * aqui e continua idêntica. Mensagem legada (autor dentro do texto, sem `group_sender`)
 * devolve `null`: a bolha degrada para o comportamento de hoje em vez de quebrar.
 */
const groupAuthor = computed(() => {
  if (!props.isGroupConversation) return null;
  if (props.messageType !== MESSAGE_TYPES.INCOMING) return null;

  return buildGroupAuthor(groupSender.value, groupAuthorLabels(t));
});

// No WhatsApp o autor abre o bloco; no Chatwoot o avatar fecha. Em grupo seguimos o
// WhatsApp — e só aqui, para não mudar um pixel da conversa 1:1.
const shouldShowGroupAuthorAvatar = computed(
  () => !!groupAuthor.value && !props.groupWithPrevious
);

const avatarInfo = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    return { name: t('CONVERSATION.BOT'), src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    return replaceInstallationName(t('CONVERSATION.NATIVE_APP_ADVISORY'));
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

provideMessageContext({
  ...toRefs(props),
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
  groupAuthor,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full mb-2 message-bubble-container"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      {
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
        'animate-message-in': props.isNew,
      },
    ]"
  >
    <label
      v-if="isForwardMode && isForwardSelectable"
      class="forward-select"
      :class="forwardSelectClass"
      @click.stop="toggleForwardSelection"
    >
      <input
        type="checkbox"
        class="forward-select__input"
        :checked="isForwardSelected"
        @change="toggleForwardSelection"
        @click.stop
      />
    </label>
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <RestrictedMessage
      v-else-if="isRestricted"
      :title="restrictedTitle"
      :subtitle="restrictedSubtitle"
      :message-type="messageType"
      :created-at="createdAt"
    />
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-1.5"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <!-- Grupo: quem aparece é o PARTICIPANTE, não o contato-grupo. O espaço fica
           reservado nas mensagens seguintes do bloco para o fio não desalinhar. -->
      <div
        v-if="groupAuthor"
        class="[grid-area:avatar] flex items-start pt-0.5"
      >
        <GroupAuthorAvatar
          v-if="shouldShowGroupAuthorAvatar"
          :author="groupAuthor"
        />
        <span v-else class="block size-7" aria-hidden="true" />
      </div>
      <div
        v-else-if="!shouldGroupWithNext && shouldShowAvatar"
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] flex items-start pt-0.5"
      >
        <Avatar
          v-bind="avatarInfo"
          :size="orientation === ORIENTATION.LEFT ? 28 : 24"
        />
      </div>
      <div
        class="[grid-area:bubble] flex relative group/message"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
          'min-w-0': variant === MESSAGE_VARIANTS.EMAIL,
          'message-failed-shake': status === MESSAGE_STATUS.FAILED,
        }"
        @contextmenu="openContextMenu($event)"
      >
        <div
          class="relative w-full group-hover/message:bg-n-alpha-1 group-hover/message:ltr:pr-8 group-hover/message:rtl:pl-8 transition-all duration-200"
        >
          <!-- Badge de Template, Encaminhada, Estrela e Mensagem agendada -->
          <div
            v-if="forwarded || starred || isScheduledMessage || templateInfo"
            class="inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-1"
          >
            <div
              v-if="isScheduledMessage"
              class="inline-flex items-center gap-1 text-n-amber-12/80"
            >
              <Icon icon="i-lucide-clock" class="size-3" />
              <span>{{ scheduledByLabel }}</span>
            </div>
            <div v-if="templateInfo" class="inline-flex items-center gap-1">
              <Icon icon="i-lucide-layout-template" class="size-3" />
              <span>{{ templateBadgeLabel }}</span>
            </div>
            <div v-if="forwarded" class="inline-flex items-center gap-1 italic">
              <Icon icon="i-lucide-forward" class="size-3" />
              <span>{{ t('CONVERSATION.FORWARDED') || 'Encaminhada' }}</span>
            </div>
            <div
              v-if="starred"
              class="inline-flex items-center gap-1 text-amber-500 dark:text-amber-400"
              :title="t('CONVERSATION.STARRED') || 'Favoritada'"
            >
              <Icon icon="i-lucide-star" class="size-3 fill-current" />
            </div>
          </div>

          <ReferralCard
            v-if="contentAttributes?.referral"
            :referral="contentAttributes.referral"
          />
          <MessageEditInput
            v-if="isEditing"
            :message-id="id"
            :conversation-id="conversationId"
            :initial-content="content"
            @close="isEditing = false"
          />
          <div v-else-if="isMessageDeleted" class="italic opacity-60">
            <Component :is="componentToRender" />
          </div>
          <Component :is="componentToRender" v-else />

          <!-- Reações recebidas do WhatsApp: chip colado na bolha reagida, no lugar da
               bolha de texto que a WAHA gerava. Vazio (e portanto inerte) em qualquer
               mensagem sem reação. -->
          <MessageReactions
            v-if="whatsappReactions.length"
            readonly
            mode="chips"
            :chips="whatsappReactions"
            :class="flexOrientationClass"
          />

          <!-- Indicador "Editado" -->
          <div
            v-if="contentAttributes.edited && !isEditing"
            class="inline-flex"
          >
            <button
              class="inline-flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors mt-0.5 select-none"
              @click.stop="
                e => {
                  historyTriggerRect = e.currentTarget.getBoundingClientRect();
                  isHistoryOpen = !isHistoryOpen;
                }
              "
            >
              <Icon icon="i-lucide-pencil-line" class="size-3" />
              <span>{{ t('CONVERSATION.EDIT_HISTORY.BADGE') }}</span>
            </button>
            <MessageEditHistory
              v-if="isHistoryOpen"
              :previous-content="contentAttributes.previousContent"
              :edited-by="contentAttributes.editedBy"
              :edited-at="contentAttributes.editedAt"
              :trigger-rect="historyTriggerRect"
              @close="isHistoryOpen = false"
            />
          </div>

          <!-- Indicador "Apagada" (só para deleções feitas por agente, que
               carregam a metadata; deleções pelo contato apenas marcam `deleted`). -->
          <div
            v-if="isMessageDeleted && contentAttributes.deletedAt"
            class="inline-flex"
          >
            <button
              class="inline-flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors mt-0.5 select-none"
              @click.stop="
                e => {
                  deleteHistoryTriggerRect =
                    e.currentTarget.getBoundingClientRect();
                  isDeleteHistoryOpen = !isDeleteHistoryOpen;
                }
              "
            >
              <Icon icon="i-lucide-trash-2" class="size-3" />
              <span>{{ t('CONVERSATION.DELETE_HISTORY.BADGE') }}</span>
            </button>
            <MessageDeleteHistory
              v-if="isDeleteHistoryOpen"
              :previous-content="contentAttributes.previousContent"
              :deleted-by="contentAttributes.deletedBy"
              :deleted-at="contentAttributes.deletedAt"
              :trigger-rect="deleteHistoryTriggerRect"
              @close="isDeleteHistoryOpen = false"
            />
          </div>

          <!-- Botão de dropdown no hover -->
          <div
            v-if="shouldShowContextMenu && isBubble"
            class="absolute ltr:right-0 rtl:left-0 top-0 bottom-0 w-10 opacity-0 group-hover/message:opacity-100 transition-opacity duration-200 ease-in-out z-[11] flex items-start justify-end pt-2 ltr:pr-1 rtl:pl-1"
            :class="{
              'bg-n-slate-4 ltr:rounded-tr-xl ltr:rounded-br-xl rtl:rounded-tl-xl rtl:rounded-bl-xl':
                variant === MESSAGE_VARIANTS.USER &&
                orientation === ORIENTATION.LEFT,
              'bg-n-solid-blue ltr:rounded-tr-xl ltr:rounded-br-sm rtl:rounded-tl-xl rtl:rounded-bl-sm':
                (variant === MESSAGE_VARIANTS.AGENT ||
                  variant === MESSAGE_VARIANTS.BOT ||
                  variant === MESSAGE_VARIANTS.TEMPLATE) &&
                orientation === ORIENTATION.RIGHT,
              'bg-n-solid-amber ltr:rounded-tr-xl ltr:rounded-br-xl rtl:rounded-tl-xl rtl:rounded-bl-xl':
                [MESSAGE_VARIANTS.PRIVATE, MESSAGE_VARIANTS.SCHEDULED].includes(
                  variant
                ),
            }"
          >
            <NextButton
              v-if="!props.private && !isScheduledMessage"
              ghost
              slate
              sm
              icon="i-lucide-corner-up-left"
              :title="$t('CONVERSATION.REPLYBOX.REPLY')"
              class="rounded-md p-1 shadow-none"
              @click.stop="handleReplyTo"
            />
            <NextButton
              ref="dropdownButtonRef"
              ghost
              slate
              sm
              icon="i-lucide-chevron-down"
              class="rounded-md p-1 shadow-none"
              @click.stop="openHoverDropdown($event)"
            />
          </div>
        </div>
      </div>
      <MessageError
        v-if="contentAttributes.externalError"
        class="[grid-area:meta] relative z-10"
        :class="flexOrientationClass"
        :error="contentAttributes.externalError"
        :is-retrying="isRetrying"
        @retry="handleRetry"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        hide-button
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
        @forward="handleForward"
        @star="handleStar"
        @unstar="handleUnstar"
        @edit="handleEdit"
      />
    </div>
  </div>
</template>

<style lang="scss">
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-sm rtl:rounded-tr-sm;
  }

  .right-bubble {
    @apply ltr:rounded-tr-sm rtl:rounded-tl-sm;
  }
}

// Failed send: a short horizontal shake (transform-only, GPU-friendly) draws
// the eye to the bubble that didn't make it. Runs once when the bubble mounts
// in the failed state; the red "Falha ao enviar" text + retry button carry the
// meaning, so the animation is purely supplementary.
.message-failed-shake {
  animation: message-shake var(--motion-slow, 360ms)
    var(--ease-in-out-quart, ease) both;
}

@keyframes message-shake {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-8px);
  }
  40% {
    transform: translateX(6px);
  }
  60% {
    transform: translateX(-4px);
  }
  80% {
    transform: translateX(2px);
  }
  100% {
    transform: translateX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .message-failed-shake {
    animation: none;
  }
}

.forward-select {
  @apply flex items-center cursor-pointer;
}

.forward-select__input {
  @apply size-4 text-n-brand rounded border-slate-300 focus:ring-n-brand;
}
</style>
